import io from 'socket.io-client';

import pkg from './../package.json';
import { isIOSEmbeddedAndStandalone } from './client';
import config from './config/app.config';
import { defaultLicence } from './constants/runtime';
import { getLang } from './i18n/l10n';
import { Licence } from './types/common';
import { storage } from './utils';

const { socketDefaultConfig } = config;

const createSocket = (licence = defaultLicence) => {
  const lang = getLang().toUpperCase();
  // Initialize socket
  const suggestedSocket = storage.localStorage.load('application:socket');
  let server = suggestedSocket?.host || socketDefaultConfig[licence].host;

  const path = suggestedSocket?.path || socketDefaultConfig[licence].path;

  if (server.indexOf('?') !== -1) {
    server = `${server}&language=${lang}&version=${pkg.version}`;
  } else {
    server = `${server}?language=${lang}&version=${pkg.version}`;
  }

  // Default transports
  let transports = ['polling', 'websocket'];

  // Get either the `WebSocket` or `MozWebSocket` globals in the browser
  const BrowserWebSocket = global.WebSocket || global.MozWebSocket;

  // Default transports in case of Android browsers
  if (BrowserWebSocket && ~navigator.userAgent.indexOf('Android')) {
    transports = ['websocket'];
  }

  if (isIOSEmbeddedAndStandalone) {
    transports = ['websocket'];
  }

  const secure = /^https/.test(server);
  const socket = io.connect(server, {
    secure,
    path,
    transports,
    forceNew: true,
  });

  socket.on('reconnect_attempt', () => {
    if (isIOSEmbeddedAndStandalone) {
      socket.io.opts.transports = ['websocket'];
    } else {
      socket.io.opts.transports = ['polling', 'websocket'];
    }
  });

  return socket;
};

interface ISocketWrapper {
  on: (event: string, fn: () => void) => void;
  emit: (event: string, ...args: Array<unknown>) => SocketIOClient.Socket;
  disconnect: (...args: Array<unknown>) => SocketIOClient.Socket;
  isConnected: () => boolean;
  createNewConnection: (licence: Licence) => void;
}

class SocketWrapper implements ISocketWrapper {
  private socket: SocketIOClient.Socket;
  private handlers: Array<[string, () => void]> = [];

  constructor() {
    this.socket = createSocket();
  }

  public on = (event: string, fn: () => void) => {
    this.handlers.push([event, fn]);
    this.socket.on(event, fn);
  };

  public emit = (event: string, ...args: Array<unknown>) => this.socket.emit(event, ...args);

  public disconnect = () => this.socket.disconnect();

  public isConnected = () => this.socket.connected;

  public createNewConnection = (licence: Licence) => {
    this.socket = createSocket(licence);
    this.handlers.forEach((handler) => this.socket.on(...handler));
  };
}

export default () => new SocketWrapper();
